/**
 * Direction Selector Component
 *
 * Handles sky direction selection interface for the Object Finder.
 * Provides UI controls for azimuth, altitude range, and field of view.
 */

export class DirectionSelector {
    constructor() {
        this.isInitialized = false;
        this.currentDirection = {
            azimuth: 180,
            minAltitude: 30,
            maxAltitude: 70,
            fieldOfView: 15
        };
        this.callbacks = {
            onDirectionChange: null
        };
    }

    /**
     * Initialize the direction selector
     */
    initialize() {
        if (this.isInitialized) {
            return;
        }

        try {
            // Slider hard limits are set in HTML:
            // Min altitude: 0-85° (max is 85 to ensure 5° gap from max)
            // Max altitude: 5-90° (min is 5 to ensure 5° gap from min)
            // Validation also adjusts values when gap is violated
            console.log('Direction Selector: Min altitude 0-85°, Max altitude 5-90°, 5° gap enforced');

            this.setupEventListeners();
            this.validateAltitudeRange(); // Set initial constraints
            this.updateDirectionDisplay();
            this.isInitialized = true;
            console.log('Direction Selector initialized');
        } catch (error) {
            console.error('Failed to initialize Direction Selector:', error);
        }
    }

    /**
     * Set up event listeners for direction controls
     */
    setupEventListeners() {
        // Azimuth controls (input and slider)
        this.setupAzimuthControls();

        // Altitude range controls (inputs and sliders)
        this.setupAltitudeControls();

        // Field of view controls (input and slider)
        this.setupFieldOfViewControls();

        // Add preset direction buttons and compass
        this.addPresetButtons();
        this.createCompassDisplay();
    }

    /**
     * Set up azimuth controls
     */
    setupAzimuthControls() {
        const azimuthInput = document.getElementById('targetAzimuth');
        const azimuthSlider = document.getElementById('azimuthSlider');

        if (azimuthInput) {
            azimuthInput.addEventListener('input', (e) => {
                const value = parseFloat(e.target.value);
                this.currentDirection.azimuth = isNaN(value) ? 180 : value;
                if (azimuthSlider) azimuthSlider.value = this.currentDirection.azimuth;
                this.updateDirectionDisplay();
                this.updateCompass();
                this.notifyDirectionChange();
            });
        }

        if (azimuthSlider) {
            azimuthSlider.addEventListener('input', (e) => {
                const value = parseFloat(e.target.value);
                this.currentDirection.azimuth = isNaN(value) ? 180 : value;
                if (azimuthInput) azimuthInput.value = this.currentDirection.azimuth;
                this.updateDirectionDisplay();
                this.updateCompass();
                this.notifyDirectionChange();
            });
        }
    }

    /**
     * Set up altitude controls
     */
    setupAltitudeControls() {
        const minAltInput = document.getElementById('minAltitude');
        const minAltSlider = document.getElementById('minAltitudeSlider');
        const maxAltInput = document.getElementById('maxAltitude');
        const maxAltSlider = document.getElementById('maxAltitudeSlider');

        if (minAltInput) {
            minAltInput.addEventListener('input', (e) => {
                const value = parseFloat(e.target.value);
                this.currentDirection.minAltitude = isNaN(value) ? 30 : value;
                if (minAltSlider) minAltSlider.value = this.currentDirection.minAltitude;
                this.validateAltitudeRange('min');
                this.updateDirectionDisplay();
                this.notifyDirectionChange();
            });
        }

        if (minAltSlider) {
            minAltSlider.addEventListener('input', (e) => {
                const value = parseFloat(e.target.value);
                this.currentDirection.minAltitude = isNaN(value) ? 30 : value;
                if (minAltInput) minAltInput.value = this.currentDirection.minAltitude;
                this.validateAltitudeRange('min');
                this.updateDirectionDisplay();
                this.notifyDirectionChange();
            });
        }

        if (maxAltInput) {
            maxAltInput.addEventListener('input', (e) => {
                const value = parseFloat(e.target.value);
                this.currentDirection.maxAltitude = isNaN(value) ? 70 : value;
                if (maxAltSlider) maxAltSlider.value = this.currentDirection.maxAltitude;
                this.validateAltitudeRange('max');
                this.updateDirectionDisplay();
                this.notifyDirectionChange();
            });
        }

        if (maxAltSlider) {
            maxAltSlider.addEventListener('input', (e) => {
                const value = parseFloat(e.target.value);
                this.currentDirection.maxAltitude = isNaN(value) ? 70 : value;
                if (maxAltInput) maxAltInput.value = this.currentDirection.maxAltitude;
                this.validateAltitudeRange('max');
                this.updateDirectionDisplay();
                this.notifyDirectionChange();
            });
        }
    }

    /**
     * Set up field of view controls
     */
    setupFieldOfViewControls() {
        const fovInput = document.getElementById('fieldOfView');
        const fovSlider = document.getElementById('fovSlider');

        if (fovInput) {
            fovInput.addEventListener('input', (e) => {
                const value = parseFloat(e.target.value);
                this.currentDirection.fieldOfView = isNaN(value) ? 15 : value;
                if (fovSlider) fovSlider.value = this.currentDirection.fieldOfView;
                this.updateDirectionDisplay();
                this.notifyDirectionChange();
            });
        }

        if (fovSlider) {
            fovSlider.addEventListener('input', (e) => {
                const value = parseFloat(e.target.value);
                this.currentDirection.fieldOfView = isNaN(value) ? 15 : value;
                if (fovInput) fovInput.value = this.currentDirection.fieldOfView;
                this.updateDirectionDisplay();
                this.notifyDirectionChange();
            });
        }
    }

    /**
     * Validate altitude range and adjust if necessary
     * Always maintains 5° minimum gap between min and max altitude
     * @param {string} changed - Which slider was changed: 'min' or 'max'
     */
    validateAltitudeRange(changed = null) {
        const minGap = 5; // Minimum gap between min and max altitude in degrees

        const minAltInput = document.getElementById('minAltitude');
        const minAltSlider = document.getElementById('minAltitudeSlider');
        const maxAltInput = document.getElementById('maxAltitude');
        const maxAltSlider = document.getElementById('maxAltitudeSlider');

        // Only adjust if the gap is actually violated
        const currentGap = this.currentDirection.maxAltitude - this.currentDirection.minAltitude;

        // If min slider was moved and gap is too small, push max up
        if (changed === 'min' && currentGap < minGap) {
            this.currentDirection.maxAltitude = Math.min(90, this.currentDirection.minAltitude + minGap);
            if (maxAltInput) maxAltInput.value = this.currentDirection.maxAltitude;
            if (maxAltSlider) maxAltSlider.value = this.currentDirection.maxAltitude;
        }

        // If max slider was moved and gap is too small, push min down
        if (changed === 'max' && currentGap < minGap) {
            this.currentDirection.minAltitude = Math.max(0, this.currentDirection.maxAltitude - minGap);
            if (minAltInput) minAltInput.value = this.currentDirection.minAltitude;
            if (minAltSlider) minAltSlider.value = this.currentDirection.minAltitude;
        }

        // General validation for programmatic changes
        if (changed === null && currentGap < minGap) {
            this.currentDirection.maxAltitude = Math.min(90, this.currentDirection.minAltitude + minGap);
            if (maxAltInput) maxAltInput.value = this.currentDirection.maxAltitude;
            if (maxAltSlider) maxAltSlider.value = this.currentDirection.maxAltitude;
        }
    }

    /**
     * Add preset direction buttons
     */
    addPresetButtons() {
        const azimuthInput = document.getElementById('targetAzimuth');
        if (azimuthInput && azimuthInput.parentNode) {
            // Create preset buttons container
            const presetsContainer = document.createElement('div');
            presetsContainer.className = 'mt-2';
            presetsContainer.innerHTML = `
                <div class="btn-group btn-group-sm" role="group" aria-label="Direction presets">
                    <button type="button" class="btn btn-outline-secondary" data-azimuth="0">N</button>
                    <button type="button" class="btn btn-outline-secondary" data-azimuth="90">E</button>
                    <button type="button" class="btn btn-outline-secondary" data-azimuth="180">S</button>
                    <button type="button" class="btn btn-outline-secondary" data-azimuth="270">W</button>
                </div>
                <small class="text-muted d-block mt-1">Quick direction presets</small>
            `;

            // Insert after the azimuth input
            azimuthInput.parentNode.appendChild(presetsContainer);

            // Add click handlers
            presetsContainer.querySelectorAll('button[data-azimuth]').forEach(button => {
                button.addEventListener('click', (e) => {
                    const azimuth = parseInt(e.target.getAttribute('data-azimuth'));
                    this.setAzimuth(azimuth);
                });
            });
        }
    }

    /**
     * Set azimuth value
     * @param {number} azimuth - Azimuth in degrees
     */
    setAzimuth(azimuth) {
        this.currentDirection.azimuth = azimuth;

        // Update input field and slider
        const azimuthInput = document.getElementById('targetAzimuth');
        const azimuthSlider = document.getElementById('azimuthSlider');

        if (azimuthInput) azimuthInput.value = azimuth;
        if (azimuthSlider) azimuthSlider.value = azimuth;

        this.updateDirectionDisplay();
        this.updateCompass();
        this.notifyDirectionChange();
    }

    /**
     * Create compass display
     */
    createCompassDisplay() {
        const compassContainer = document.querySelector('#compassDisplay .compass-container');
        if (compassContainer) {
            compassContainer.innerHTML = `
                <div class="compass-directions">
                    <div class="compass-direction north">N</div>
                    <div class="compass-direction east">E</div>
                    <div class="compass-direction south">S</div>
                    <div class="compass-direction west">W</div>
                </div>
                <div class="compass-needle" id="compassNeedle"></div>
            `;

            // Initial compass update
            this.updateCompass();
        }
    }

    /**
     * Update compass needle
     */
    updateCompass() {
        const needle = document.getElementById('compassNeedle');
        if (needle) {
            // Rotate needle to show azimuth direction
            const rotation = this.currentDirection.azimuth;
            needle.style.transform = `translate(-50%, -100%) rotate(${rotation}deg)`;
        }
    }

    /**
     * Update direction display
     */
    updateDirectionDisplay() {
        const compassDirection = this.getCompassDirection(this.currentDirection.azimuth);
        const directionDisplay = document.getElementById('directionDisplay');

        if (directionDisplay) {
            const altitudeRange = this.currentDirection.maxAltitude - this.currentDirection.minAltitude;
            const avgAltitude = (this.currentDirection.minAltitude + this.currentDirection.maxAltitude) / 2;

            directionDisplay.innerHTML = `
                <div class="direction-display-grid">
                    <div class="direction-display-item">
                        <div class="direction-display-value">${this.currentDirection.azimuth}°</div>
                        <div class="direction-display-label">${compassDirection} Direction</div>
                    </div>
                    <div class="direction-display-item">
                        <div class="direction-display-value">${this.currentDirection.minAltitude}° - ${this.currentDirection.maxAltitude}°</div>
                        <div class="direction-display-label">Altitude Range (${altitudeRange}° span)</div>
                    </div>
                    <div class="direction-display-item">
                        <div class="direction-display-value">±${this.currentDirection.fieldOfView}°</div>
                        <div class="direction-display-label">Search Cone</div>
                    </div>
                </div>
                <div class="mt-2 text-center">
                    <small class="text-muted">
                        <i class="fas fa-crosshairs"></i>
                        Target: ${compassDirection} (${this.currentDirection.azimuth}°) at ${avgAltitude.toFixed(0)}° average altitude
                    </small>
                </div>
            `;
        }
    }

    /**
     * Get compass direction from azimuth
     * @param {number} azimuth - Azimuth in degrees
     * @returns {string} Compass direction
     */
    getCompassDirection(azimuth) {
        const directions = [
            'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
            'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
        ];
        const index = Math.round(azimuth / 22.5) % 16;
        return directions[index];
    }

    /**
     * Get current direction settings
     * @returns {Object} Current direction
     */
    getCurrentDirection() {
        return { ...this.currentDirection };
    }

    /**
     * Set direction settings
     * @param {Object} direction - Direction settings
     */
    setDirection(direction) {
        if (direction.azimuth !== undefined) {
            this.currentDirection.azimuth = direction.azimuth;
            const azimuthInput = document.getElementById('targetAzimuth');
            const azimuthSlider = document.getElementById('azimuthSlider');
            if (azimuthInput) azimuthInput.value = direction.azimuth;
            if (azimuthSlider) azimuthSlider.value = direction.azimuth;
        }

        if (direction.minAltitude !== undefined) {
            this.currentDirection.minAltitude = direction.minAltitude;
            const minAltInput = document.getElementById('minAltitude');
            const minAltSlider = document.getElementById('minAltitudeSlider');
            if (minAltInput) minAltInput.value = direction.minAltitude;
            if (minAltSlider) minAltSlider.value = direction.minAltitude;
        }

        if (direction.maxAltitude !== undefined) {
            this.currentDirection.maxAltitude = direction.maxAltitude;
            const maxAltInput = document.getElementById('maxAltitude');
            const maxAltSlider = document.getElementById('maxAltitudeSlider');
            if (maxAltInput) maxAltInput.value = direction.maxAltitude;
            if (maxAltSlider) maxAltSlider.value = direction.maxAltitude;
        }

        if (direction.fieldOfView !== undefined) {
            this.currentDirection.fieldOfView = direction.fieldOfView;
            const fovInput = document.getElementById('fieldOfView');
            const fovSlider = document.getElementById('fovSlider');
            if (fovInput) fovInput.value = direction.fieldOfView;
            if (fovSlider) fovSlider.value = direction.fieldOfView;
        }

        this.validateAltitudeRange();
        this.updateDirectionDisplay();
        this.updateCompass();
        this.notifyDirectionChange();
    }

    /**
     * Set callback for direction changes
     * @param {Function} callback - Callback function
     */
    onDirectionChange(callback) {
        this.callbacks.onDirectionChange = callback;
    }

    /**
     * Notify about direction changes
     */
    notifyDirectionChange() {
        if (this.callbacks.onDirectionChange) {
            this.callbacks.onDirectionChange(this.getCurrentDirection());
        }
    }

    /**
     * Convert direction to readable string
     * @returns {string} Direction description
     */
    getDirectionDescription() {
        const compassDir = this.getCompassDirection(this.currentDirection.azimuth);
        return `${compassDir} (${this.currentDirection.azimuth}°) at ${this.currentDirection.minAltitude}°-${this.currentDirection.maxAltitude}° altitude`;
    }

    /**
     * Check if current direction is valid
     * @returns {Object} Validation result
     */
    validateDirection() {
        const errors = [];

        if (this.currentDirection.azimuth < 0 || this.currentDirection.azimuth > 360) {
            errors.push('Azimuth must be between 0 and 360 degrees');
        }

        if (this.currentDirection.minAltitude < 0 || this.currentDirection.minAltitude > 90) {
            errors.push('Minimum altitude must be between 0 and 90 degrees');
        }

        if (this.currentDirection.maxAltitude < 0 || this.currentDirection.maxAltitude > 90) {
            errors.push('Maximum altitude must be between 0 and 90 degrees');
        }

        if (this.currentDirection.minAltitude >= this.currentDirection.maxAltitude) {
            errors.push('Minimum altitude must be less than maximum altitude');
        }

        if (this.currentDirection.fieldOfView < 1 || this.currentDirection.fieldOfView > 90) {
            errors.push('Search radius must be between 1 and 90 degrees');
        }

        return {
            valid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Reset to default direction
     */
    resetToDefault() {
        this.setDirection({
            azimuth: 180,
            minAltitude: 30,
            maxAltitude: 70,
            fieldOfView: 15
        });
    }
}

export default DirectionSelector;